/* eslint-disable no-console */
import { PublicClientApplication } from "@azure/msal-browser";
import jwtDecode from "jwt-decode";

// MSAL configuration pulled from env variables (see .env.*)
const msalConfig = {
  auth: {
    authority: process.env.REACT_APP_AADB2C_AUTHORITY,
    clientId: process.env.REACT_APP_AADB2C_CLIENT_ID,
    knownAuthorities: [process.env.REACT_APP_AADB2C_KNOWN_AUTHORITY], // Required for B2C
    redirectUri: process.env.REACT_APP_BASE_URL,
    postLogoutRedirectUri: process.env.REACT_APP_BASE_URL,
  },
  cache: {
    // localStorage (not sessionStorage) so the session is shared across tabs.
    // A new tab then reuses the cached token / silently refreshes instead of
    // forcing a loginRedirect -- which keeps the login beacon firing only on
    // real sign-ins, not on every tab open.
    cacheLocation: "localStorage",
    storeAuthStateInCookie: false,
  },
};

// Resolve API scopes; must include the API resource scope to obtain an access_token usable for backend calls
function resolveScopes() {
  const scopesFromEnv = (process.env.REACT_APP_AADB2C_SCOPES || "").trim();
  if (scopesFromEnv.length > 0) return scopesFromEnv.split(/\s+/g);

  throw new Error("No scopes provided");
}

const loginRequest = {
  scopes: resolveScopes(),
};

const tokenRequest = {
  scopes: resolveScopes(),
};

const msalInstance = new PublicClientApplication(msalConfig);

// Track redirect handling readiness for callers that need to await it
let msalInitCompleted = false;
let msalInitPromiseResolve = null;
const msalInitPromise = new Promise((resolve) => {
  msalInitPromiseResolve = resolve;
});

// Best-effort "a real login happened" beacon. Only called from the interactive
// redirect-completion path below (silent token refresh goes through
// acquireTokenSilent, not handleRedirectPromise), so the backend records one
// login per actual sign-in. Fire-and-forget: never block or fail login on it.
function notifyBackendLogin(accessToken) {
  if (!accessToken) return;
  const base = (process.env.REACT_APP_OPENID_AUTHORITY || "").replace(
    /\/$/,
    "",
  );
  if (!base) return;
  fetch(`${base}/api/users/login`, {
    method: "POST",
    headers: { Authorization: `Bearer ${accessToken}` },
  }).catch(() => {});
}

// Process the redirect callback (if any) on page load so that tokens are cached.
// This handles both login and logout redirects when they come back to the root URL
msalInstance
  .handleRedirectPromise()
  .then((result) => {
    if (result && result.account) {
      msalInstance.setActiveAccount(result.account);
      // A real interactive login just completed -> tell the backend to record it
      // (UserLoginHistory row + LastLoginDate). Uses the access token from this
      // redirect result; deduped server-side.
      notifyBackendLogin(result.accessToken);
    } else {
      const accounts = msalInstance.getAllAccounts();
      if (accounts && accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0]);
      }
    }
    msalInitCompleted = true;
    if (msalInitPromiseResolve) msalInitPromiseResolve();
  })
  .catch((err) => {
    // Was discarded, which hid redirect-handling failures completely.
    console.error("MSAL redirect handling failed", err);
    msalInitCompleted = true;
    if (msalInitPromiseResolve) msalInitPromiseResolve();
  });

/*
 * Helper: builds a user-like object compatible with the previous oidc-client usage so that the rest of the app
 * can continue working with minimal refactoring.
 */
function buildUserFromResult(authResult) {
  if (!authResult) return null;

  const { idTokenClaims, accessToken, expiresOn } = authResult;
  const decodedIdToken = idTokenClaims ?? jwtDecode(authResult.idToken ?? "");

  const built = {
    access_token: accessToken,
    expires_at: expiresOn?.getTime() ?? 0,
    expired: expiresOn ? expiresOn.getTime() <= Date.now() : true,
    profile: decodedIdToken,
  };
  // Try to parse state back (we stored JSON via loginRedirect state)
  try {
    if (typeof authResult.state === "string" && authResult.state.length > 0) {
      // MSAL can prepend its own metadata; split on '|'
      const parts = authResult.state.split("|");
      const rawAppState = parts.length > 1 ? parts[1] : parts[0];
      try {
        built.state = JSON.parse(rawAppState);
      } catch (_) {
        // ignore parse error
      }
    }
  } catch (_) {
    // ignore state issues
  }
  return built;
}

async function getUser() {
  const accounts = msalInstance.getAllAccounts();
  if (!accounts || accounts.length === 0) return null;

  const silentRequest = {
    ...tokenRequest,
    account: accounts[0],
  };

  try {
    const result = await msalInstance.acquireTokenSilent(silentRequest);
    return buildUserFromResult(result);
  } catch (error) {
    // ANY silent failure means "no usable session" -> tell callers to fall back
    // to an interactive redirect. Besides InteractionRequiredAuthError this
    // covers BrowserAuthError: monitor_window_timeout, which B2C triggers
    // whenever the hidden-iframe refresh can't complete -- b2clogin.com sends
    // X-Frame-Options: deny on its login page, and browsers that block
    // third-party storage (Edge Tracking Prevention, Safari ITP) drop the B2C
    // session cookie so the iframe always lands on that login page.
    // Rethrowing left Auth.componentDidMount with an unhandled rejection: no
    // setState, no signinRedirect, render() -> null == permanent blank page.
    // console.error, not console.warn: Application Insights only wraps
    // console.error (see applicationiIsightsTracking.js), and this is the one
    // failure that costs a user their unsaved work. Logged at the level we
    // actually collect so it shows up in telemetry instead of only in a
    // console nobody is watching.
    console.error(
      "Silent token acquisition failed; interactive login required",
      error,
    );
    return null;
  }
}

async function signinRedirect(opts) {
  if (isInteractionInProgress()) return;
  await msalInstance.loginRedirect({
    ...loginRequest,
    state: JSON.stringify(opts?.state),
  });
}

async function signinSilent() {
  const accounts = msalInstance.getAllAccounts();
  if (!accounts || accounts.length === 0) {
    throw new Error("No account in cache");
  }
  const result = await msalInstance.acquireTokenSilent({
    ...tokenRequest,
    account: accounts[0],
  });
  return buildUserFromResult(result);
}

async function signoutRedirect() {
  await msalInstance.logoutRedirect();
}

async function signinRedirectCallback() {
  const result = await msalInstance.handleRedirectPromise();
  return buildUserFromResult(result);
}

function signinSilentCallback() {
  return Promise.resolve();
}

function isInteractionInProgress() {
  try {
    return (
      sessionStorage.getItem("msal.interaction.status") ===
      "interaction_in_progress"
    );
  } catch (_) {
    return false;
  }
}

async function waitForReady() {
  if (msalInitCompleted) return;
  await msalInitPromise;
}

// Export an object mimicking the old oidc-client API
const userManager = {
  getUser,
  signinRedirect,
  signinSilent,
  signoutRedirect,
  signinRedirectCallback,
  signinSilentCallback,
  isInteractionInProgress,
  waitForReady,
};

export default userManager;
